import { formatDateTime } from "@/api/datetime";
import { AddWorkerDialog } from "@/components/AddWorkerDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { useAuth } from "@/contexts/AuthContext";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useUsers } from "@/features/users/hooks/useUsers";
import { useAllWorkers } from "@/features/workers/hooks/useWorkers";
import { formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { Timestamp } from "firebase/firestore";
import { ArrowUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import {
  isWorkerAssignedToProject,
  type ProjectModel,
  type WorkerModel,
} from "../../../firestore_types";

export const Route = createFileRoute("/dashboard/agency/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { user } = useAuth();
  const { workers, loading } = useAllWorkers();
  const { users } = useUsers();
  const { projects } = useProjects();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const navigate = useNavigate();

  // Filter workers by current agency user's ID
  const agencyWorkers = useMemo(() => {
    if (!workers || !user?.uid) {
      return [];
    }

    return [...workers]
      .filter((worker) => worker.agencyId === user.uid)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [workers, user]);

  const assignedProjectsMap = useMemo(() => {
    const map = new Map<string, ProjectModel[]>();
    if (!agencyWorkers || !projects) {
      return map;
    }

    for (const worker of agencyWorkers) {
      map.set(
        worker.id,
        projects.filter((project) => isWorkerAssignedToProject(project, worker.id)),
      );
    }

    return map;
  }, [projects, agencyWorkers]);

  // Helper functions
  const getManagerName = (managerId?: string) => {
    if (!managerId) {
      return "No Manager";
    }
    const manager = users?.find((user) => user.uid === managerId && user.type === "manager");
    return manager?.name || "Unknown Manager";
  };

  const getAssignedProjects = (workerId: string) => {
    return assignedProjectsMap.get(workerId) || [];
  };

  const getWorkerInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleWorkerAdded = (workerId?: string) => {
    if (workerId) {
      navigate({
        to: "/dashboard/agency/workers/$workerId",
        params: { workerId },
      });
    }
  };

  const columns = useMemo<ColumnDef<WorkerModel>[]>(
    () => [
      {
        id: "all",
        accessorFn: (worker) => {
          const assignedProjects = getAssignedProjects(worker.id);
          const parts = [
            worker.name,
            worker.phoneNumber,
            worker.email ?? "",
            getManagerName(worker.managerId),
            ...(worker.specialties ?? []),
            ...assignedProjects.map((project) => project.name),
          ];
          return parts.filter(Boolean).join(" ");
        },
        header: () => null,
        cell: () => null,
        enableSorting: false,
        enableHiding: true,
      },
      {
        accessorKey: "name",
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Worker
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const worker = row.original;

          return (
            <Link
              to="/dashboard/agency/workers/$workerId"
              params={{ workerId: worker.id }}
              className="flex items-center gap-3 hover:opacity-80"
            >
              <Avatar>
                <AvatarImage src={worker.profilePicture || undefined} alt={worker.name} />
                <AvatarFallback className="text-xs font-medium">
                  {getWorkerInitials(worker.name)}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1 text-sm">
                <div className="font-medium hover:text-blue-600">{worker.name}</div>
                {worker.type === "app_worker" && (
                  <div className="text-muted-foreground text-xs">{worker.email}</div>
                )}
                <div className="text-muted-foreground text-xs">
                  {formatPhoneNumber(worker.phoneNumber)}
                </div>
              </div>
            </Link>
          );
        },
      },
      {
        id: "manager",
        accessorFn: (worker) => getManagerName(worker.managerId),
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Manager
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <div className="text-sm">{getManagerName(row.original.managerId)}</div>,
      },
      {
        id: "gender",
        accessorKey: "gender",
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Gender
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div className="text-sm">{row.original.gender || "-"}</div>
        ),
      },
      {
        id: "projectCount",
        accessorFn: (worker) => getAssignedProjects(worker.id).length,
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Assigned Projects
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div className="text-sm font-medium">{getAssignedProjects(row.original.id).length}</div>
        ),
      },
      {
        id: "specialties",
        header: "Specialties",
        cell: ({ row }) => {
          const specialties = row.original.specialties ?? [];

          if (specialties.length === 0) {
            return <span className="text-muted-foreground text-xs italic">None</span>;
          }

          return (
            <div className="flex max-w-32 flex-wrap gap-1">
              {specialties.slice(0, 3).map((specialty) => (
                <Badge key={specialty} variant="outline" className="text-xs">
                  {specialty}
                </Badge>
              ))}
              {specialties.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{specialties.length - 3}
                </Badge>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "createdAt",
        accessorFn: (worker) =>
          worker.createdAt ? ((worker.createdAt as Timestamp).toMillis?.() ?? 0) : 0,
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Created
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <div className="text-sm">{formatDateTime(row.original.createdAt)}</div>,
      },
    ],
    [getAssignedProjects, getManagerName],
  );

  return (
    <div className="space-y-6 p-1 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold">My Workers</h1>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-primary hover:bg-primary/80 rounded px-4 py-2 text-sm text-white"
        >
          Add Worker
        </button>
      </div>

      <DataTable<WorkerModel, unknown>
        columns={columns}
        data={agencyWorkers}
        isLoading={loading}
        disablePagination={true}
        searchColumnId="all"
        initialVisibility={{ all: false }}
        emptyMessage="No workers found for your agency."
      />

      <AddWorkerDialog
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        agencyId={user?.uid}
        showPayRateField
        showBillingRateField={false}
        showSpecialtiesField
        onSuccess={handleWorkerAdded}
      />
    </div>
  );
}
